import type { FractalSettings } from "../types/fractal";
import type { KaleidoscopeSettings } from "../types/kaleidoscope";
import type { BlisterSettings } from "../types/settings";
import { normalizeBlisterSettings } from "./blister-settings";
import { normalizeFractalSettings } from "./fractal-settings";
import { normalizeKaleidoscopeSettings } from "./kaleidoscope-settings";

export type PerformanceTier = "battery" | "balanced" | "high";

export const PERFORMANCE_TIERS: { id: PerformanceTier; label: string; description: string }[] = [
  { id: "balanced", label: "Balanced", description: "Good default for laptops and modern phones." },
  { id: "battery", label: "Battery", description: "Lower glow and density for mobile comfort." },
  { id: "high", label: "High", description: "Fuller effects for desktop sessions." },
];

function scaleByTier(tier: PerformanceTier, battery: number, balanced: number, high: number) {
  if (tier === "battery") return battery;
  if (tier === "high") return high;
  return balanced;
}

export function applyBlisterPerformanceTier(settings: BlisterSettings, tier: PerformanceTier): BlisterSettings {
  const densityScale = scaleByTier(tier, 0.58, 0.82, 1);
  const effectScale = scaleByTier(tier, 0.62, 0.84, 1);

  return normalizeBlisterSettings({
    ...settings,
    count: Math.round(settings.count * densityScale),
    sparkleCount: Math.round(settings.sparkleCount * densityScale),
    glowStrength: settings.glowStrength * effectScale,
    blurStrength: settings.blurStrength * effectScale,
    atmosphereIntensity: settings.atmosphereIntensity * scaleByTier(tier, 0.78, 0.92, 1),
    hazeAmount: settings.hazeAmount * scaleByTier(tier, 0.75, 0.9, 1),
    trailAmount: settings.trailAmount * effectScale,
    afterimageAmount: settings.afterimageAmount * effectScale,
    bloomIntensity: settings.bloomIntensity * effectScale,
    grainAmount: settings.grainAmount * scaleByTier(tier, 0.35, 0.7, 1),
    chromaticAberration: settings.chromaticAberration * scaleByTier(tier, 0.35, 0.65, 1),
  });
}

export function applyKaleidoscopePerformanceTier(
  settings: KaleidoscopeSettings,
  tier: PerformanceTier
): KaleidoscopeSettings {
  const detailScale = scaleByTier(tier, 0.58, 0.82, 1);
  const effectScale = scaleByTier(tier, 0.55, 0.82, 1);

  return normalizeKaleidoscopeSettings({
    ...settings,
    facetDensity: settings.facetDensity * detailScale,
    patternComplexity: settings.patternComplexity * scaleByTier(tier, 0.72, 0.9, 1),
    shimmerSpeed: settings.shimmerSpeed * scaleByTier(tier, 0.72, 0.9, 1),
    pulseStrength: settings.pulseStrength * effectScale,
    glowStrength: settings.glowStrength * effectScale,
    bloomRadius: settings.bloomRadius * scaleByTier(tier, 0.72, 0.88, 1),
    refractionStrength: settings.refractionStrength * effectScale,
    sparkleDensity: settings.sparkleDensity * detailScale,
    microFacetStrength: settings.microFacetStrength * detailScale,
    textureAmount: settings.textureAmount * detailScale,
  });
}

export function applyFractalPerformanceTier(settings: FractalSettings, tier: PerformanceTier): FractalSettings {
  const effectScale = scaleByTier(tier, 0.62, 0.84, 1);
  const detailScale = scaleByTier(tier, 0.72, 0.88, 1);

  return normalizeFractalSettings({
    ...settings,
    glow: settings.glow * effectScale,
    grain: settings.grain * scaleByTier(tier, 0.35, 0.7, 1),
    shimmer: settings.shimmer * effectScale,
    fieldWarp: settings.fieldWarp * scaleByTier(tier, 0.72, 0.88, 1),
    detail: settings.detail * detailScale,
    fractalDepth: settings.fractalDepth * scaleByTier(tier, 0.82, 0.92, 1),
  });
}
